<template>
  <div class="cadPricingNotice" role="status">
    <div class="cadPricingNoticeInner">
      <div class="cadPricingNoticeCopy">
        <div class="cadPricingNoticeTitle">
          <span aria-hidden="true">🇨🇦</span>
          <span>{{ $t('packages.cadNotice.title') }}</span>
        </div>
        <div class="cadPricingNoticeBody">
          {{ $t('packages.cadNotice.body') }}
        </div>
      </div>

      <div
        v-if="conversionRate != null"
        class="cadConversionCard"
      >
        <div class="cadConversionIcon" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 7h11l-1.5-1.5L18 4l4 4-4 4-1.5-1.5L18 9H7V7z" fill="currentColor"/>
            <path d="M17 17H6l1.5 1.5L6 20l-4-4 4-4 1.5 1.5L6 15h11v2z" fill="currentColor"/>
          </svg>
        </div>
        <div class="cadConversionContent">
          <div class="cadConversionTitle">
            {{ $t('packages.cadNotice.rateTitle') }}
          </div>
          <div class="cadConversionRate en_num" dir="ltr">
            {{ $t('packages.cadNotice.rateValue', { rate: formattedRate }) }}
          </div>
          <div
            v-if="updatedAtLabel"
            class="cadConversionUpdated"
          >
            {{ updatedAtLabel }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import momentJalali from 'moment-jalaali';

export default {
  name: 'CadPricingNotice',
  data() {
    return {
      conversionRate: null,
      updatedAt: null,
    };
  },
  computed: {
    formattedRate() {
      if (this.conversionRate == null) {
        return '';
      }
      const amount = Number(this.conversionRate);
      if (Number.isNaN(amount)) {
        return this.conversionRate;
      }
      return amount.toFixed(2).replace(/\.00$/, "");
    },
    updatedAtLabel() {
      if (!this.updatedAt) {
        return null;
      }
      const date = new Date(this.updatedAt);
      if (Number.isNaN(date.getTime())) {
        return null;
      }

      const now = new Date();
      const isToday = date.getFullYear() === now.getFullYear()
        && date.getMonth() === now.getMonth()
        && date.getDate() === now.getDate();

      if (isToday) {
        return this.$t('packages.cadNotice.lastUpdatedToday');
      }

      return this.$t('packages.cadNotice.lastUpdated', {
        date: this.formatUpdatedDay(date),
      });
    },
  },
  methods: {
    formatUpdatedDay(date) {
      if (this.$i18n.locale === 'fa') {
        momentJalali.loadPersian({dialect: 'persian-modern', usePersianDigits: true});
        return momentJalali(date).format('jD jMMMM jYYYY');
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },
    loadCadConversionRate() {
      this.requesting('currency', 'getCadConversionRate', {}, {}, {})
        .then((resp) => {
          const conversion = resp && resp.data && resp.data.conversion
            ? resp.data.conversion
            : null;
          if (!conversion || conversion.conversion_rate == null) {
            return;
          }
          this.conversionRate = conversion.conversion_rate;
          this.updatedAt = conversion.updated_at || null;
        })
        .catch(() => {
          // Keep the reassurance notice visible even if the rate request fails.
          this.conversionRate = null;
          this.updatedAt = null;
        });
    },
  },
  mounted() {
    this.loadCadConversionRate();
  },
};
</script>

<style scoped>
.cadPricingNotice {
  background: #e8f4fc;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 24px;
  color: #1a1a1a;
}

.cadPricingNoticeInner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.cadPricingNoticeCopy {
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}

.cadPricingNoticeTitle {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 10px;
}

.cadPricingNoticeBody {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.7;
  color: #4a4a4a;
}

.cadConversionCard {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 220px;
  max-width: 280px;
  background: #ffffff;
  border: 1px solid #dbe4ed;
  border-radius: 12px;
  padding: 14px 16px;
}

.cadConversionIcon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e8f8f1;
  color: #39c990;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.cadConversionContent {
  min-width: 0;
}

.cadConversionTitle {
  color: #616161;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  margin-bottom: 4px;
}

.cadConversionRate {
  color: #212121;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  font-family: Roboto, sans-serif;
  direction: ltr;
  unicode-bidi: isolate;
  text-align: left;
}

.cadConversionUpdated {
  margin-top: 4px;
  color: #9e9e9e;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
}

@media (max-width: 960px) {
  .cadPricingNoticeInner {
    flex-direction: column;
    align-items: stretch;
  }

  .cadPricingNoticeCopy {
    text-align: center;
  }

  .cadPricingNoticeTitle {
    justify-content: center;
  }

  .cadConversionCard {
    max-width: none;
    width: 100%;
  }
}
</style>
